/**
 * Progression System Utilities
 * Runtime functions and constants for XP, leveling, and stat allocation.
 * Type definitions remain in src/types/progression.ts.
 */

import type { EntityStats, EntityProgression, StatMultipliers } from '@/types';

/**
 * XP rewards based on fish quality (matches Quality enum values)
 */
export const XP_BY_QUALITY: Record<string, number> = {
  common: 1,
  uncommon: 3,
  rare: 7,
  epic: 15,
  legendary: 30,
  unique: 50,
  quest: 25,
};

/**
 * Base stats for new entities
 * Start at 0 to make progression feel more rewarding
 */
export const DEFAULT_STATS: EntityStats = {
  eyesight: 0,
  reflexes: 0,
  finesse: 0,
  physique: 0,
  luck: 0,
};

/**
 * Calculate XP required for a given level
 * Formula: 10 * (level ^ 1.8)
 */
export function calculateXPForLevel(level: number): number {
  return Math.floor(10 * Math.pow(level, 1.8));
}

/**
 * Convert entity stats to gameplay multipliers
 * Each stat point = ~0.5-1.5% improvement (incremental progression)
 */
export function calculateStatMultipliers(stats: EntityStats): StatMultipliers {
  // Luck provides tiny bonus to everything (0.2% per point)
  const luckBonus = stats.luck * 0.002;

  return {
    // Eyesight (1%) + Physique (0.5%) + Luck
    catchSpeed: 1 + (stats.eyesight * 0.01) + (stats.physique * 0.005) + luckBonus,

    // Reflexes (1.5%) + Eyesight (0.5%) + Luck
    catchChance: 1 + (stats.reflexes * 0.015) + (stats.eyesight * 0.005) + luckBonus,

    // Finesse (1.5%) + Luck - affects quality roll
    qualityBonus: (stats.finesse * 0.015) + luckBonus,

    // Physique (1%) + Luck
    valueBonus: (stats.physique * 0.01) + luckBonus,
  };
}

/**
 * Check if entity leveled up after gaining XP
 * Returns number of levels gained
 */
export function checkLevelUp(currentLevel: number, currentXP: number): number {
  let levelsGained = 0;
  let level = currentLevel;
  let remainingXP = currentXP;

  while (remainingXP >= calculateXPForLevel(level + 1)) {
    const xpNeeded = calculateXPForLevel(level + 1);
    remainingXP -= xpNeeded;
    level++;
    levelsGained++;
  }

  return levelsGained;
}

/**
 * Award XP to an entity and handle level-ups
 * Returns updated progression with new level, XP, and stat points
 */
export function awardXP(progression: EntityProgression, xpGained: number): EntityProgression {
  const newXP = progression.progress.currentXP + xpGained;
  const levelsGained = checkLevelUp(progression.progress.level, newXP);

  if (levelsGained > 0) {
    const newLevel = progression.progress.level + levelsGained;

    // Calculate remaining XP after level-ups
    let remainingXP = newXP;
    for (let i = progression.progress.level + 1; i <= newLevel; i++) {
      remainingXP -= calculateXPForLevel(i);
    }

    return {
      stats: progression.stats,
      progress: {
        level: newLevel,
        currentXP: remainingXP,
        xpToNextLevel: calculateXPForLevel(newLevel + 1),
        unspentStatPoints: progression.progress.unspentStatPoints + levelsGained,
      },
    };
  }

  // No level up, just add XP
  return {
    stats: progression.stats,
    progress: {
      ...progression.progress,
      currentXP: newXP,
    },
  };
}

/**
 * Allocate a stat point to a specific stat
 * Returns updated progression if valid, undefined if invalid
 */
export function allocateStatPoint(
  progression: EntityProgression,
  stat: keyof EntityStats
): EntityProgression | undefined {
  if (progression.progress.unspentStatPoints <= 0) {
    return undefined; // No points to spend
  }

  return {
    stats: {
      ...progression.stats,
      [stat]: progression.stats[stat] + 1,
    },
    progress: {
      ...progression.progress,
      unspentStatPoints: progression.progress.unspentStatPoints - 1,
    },
  };
}
